import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Col,
  Grid,
  Row,
} from "react-bootstrap";
import NotificationSystem from "react-notification-system";
import ScreenLoader from "views/Components/screenLoader";
import Button from "../components/SurveyButton";
import SurveyActionMenu from "../components/SurveyActionMenu";
import SurveyConfirmationModal from "../components/SurveyConfirmationModal";
import { SurveyStatusBadge as Status } from "../components/SurveyStatusBadge";
import SurveyBuilderWorkspace from "../builder/SurveyBuilderWorkspace";
import SurveyOverviewWorkspace from "../overview/SurveyOverviewWorkspace";
import SurveyKpiGrid from "../overview/SurveyKpiGrid";
import SurveySubmissionDetailModal from "../overview/SurveySubmissionDetailModal";
import SurveySubmissionTable from "../overview/SurveySubmissionTable";
import SurveyTestDeploymentModal from "../test-deployment/SurveyTestDeploymentModal";
import {
  apiErrorMessage,
  createSurvey,
  deleteSurvey,
  getSurvey,
  getSurveyOverview,
  updateSurvey,
} from "api/survey/surveyApi";
import {
  duplicateSurveyForm,
  surveyPayload,
  surveyToForm,
} from "../domain/survey.mappers";
import { validateDraftSurvey, validatePublishableSurvey } from "../domain/survey.validation";
import useUnsavedSurveyWarning from "../hooks/useUnsavedSurveyWarning";
import useSurveySubmissions from "../hooks/useSurveySubmissions";
import useSurveyEditor from "../hooks/useSurveyEditor";
import useSurveyLifecycle from "../hooks/useSurveyLifecycle";
import "../styles/index.css";

const emptyOverview = { eligibleLeadCount: 0, deliveredLeadCount: 0, answeredLeadCount: 0, pendingLeadCount: 0, responseRate: 0 };

const SurveyDetailPage = ({ history, location, match }) => {
  const notificationRef = useRef(null);
  const surveyId = match.params.surveyId;
  const isNew = !surveyId;
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const duplicateDraft = isNew && location.state ? location.state.duplicateDraft : null;
  const [tab, setTab] = useState(requestedTab === "submissions" ? "submissions" : "overview");
  const [overview, setOverview] = useState(emptyOverview);
  const [testDeploymentOpen, setTestDeploymentOpen] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const notify = useCallback((message, level = "success") => {
    if (notificationRef.current) notificationRef.current.addNotification({ message, level, position: "bl", autoDismiss: 5 });
  }, []);

  const {
    form,
    setForm,
    selectedQuestionId,
    setSelectedQuestionId,
    formErrors,
    dirty,
    setDirty,
    updateForm,
    showValidation,
  } = useSurveyEditor({ duplicateDraft, notify });
  const unblockRef = useUnsavedSurveyWarning({ dirty, history });
  const { confirmation, setConfirmation, confirmAction } = useSurveyLifecycle({
    onPublish: () => performPublish(),
    onInactivate: () => performInactivate(),
    onDelete: () => performRemove(),
  });

  const {
    submissions,
    submissionDetail,
    setSubmissionDetail,
    submissionsLoading,
    detailLoading,
    loadSubmissions,
    openSubmission,
  } = useSurveySubmissions({ surveyId, enabled: tab === "submissions" && !isNew, notify });

  const loadSurvey = async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const surveyResponse = await getSurvey(surveyId);
      const nextForm = surveyToForm(surveyResponse.data);
      setForm(nextForm);
      setSelectedQuestionId(nextForm.status === "draft" ? ((nextForm.questions || [])[0] || {}).id || null : "home");
      if (nextForm.status === "draft") setTab("overview");
      else setTab(requestedTab === "submissions" ? "submissions" : "overview");
      if (nextForm.status !== "draft") {
        const overviewResponse = await getSurveyOverview(surveyId);
        setOverview({ ...emptyOverview, ...overviewResponse.data });
      }
      setDirty(false);
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to load the survey."), "error");
    }
    setLoading(false);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { loadSurvey(); }, [surveyId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const leaveFor = (path, replace = false) => {
    if (unblockRef.current) unblockRef.current();
    setDirty(false);
    if (replace) history.replace(path); else history.push(path);
  };

  const saveDraft = async () => {
    const validation = validateDraftSurvey(form);
    if (!validation.isValid) {
      showValidation(validation, "Please correct the highlighted survey fields.");
      return;
    }
    setSaving(true);
    try {
      const response = isNew ? await createSurvey(surveyPayload(form)) : await updateSurvey(surveyId, surveyPayload(form));
      setForm(surveyToForm(response.data));
      setDirty(false);
      notify(isNew ? "Survey draft created successfully." : "Survey draft updated successfully.");
      if (isNew) leaveFor(`/admin/surveys/${response.data.id}`, true);
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to save the survey draft."), "error");
    }
    setSaving(false);
  };

  const publish = () => {
    const validation = validatePublishableSurvey(form);
    if (!validation.isValid) {
      showValidation(validation, "Complete the survey details and questions before publishing.");
      return;
    }
    setConfirmation({
      action: "publish",
      title: "Publish Survey?",
      message: `Publish “${form.title}”? Published surveys cannot be edited.`,
      confirmLabel: "Publish",
      confirmStyle: "success",
    });
  };

  const openTestDeployment = () => {
    if (dirty) {
      notify("Save your draft changes before deploying it for testing.", "warning");
      return;
    }
    const validation = validatePublishableSurvey(form);
    if (!validation.isValid) {
      showValidation(validation, "Complete the survey details and questions before deploying a test.");
      return;
    }
    setTestDeploymentOpen(true);
  };

  const testDeployed = (result) => {
    setForm((current) => ({ ...current, testLeadCount: Number(result.totalTestLeadCount || 0) }));
    const count = Number(result.assignedCount || 0);
    notify(count ? `Survey deployed to ${count} test lead${count === 1 ? "" : "s"}.` : "All selected leads were already assigned.");
  };

  const testAssignmentRemoved = (result) => {
    setForm((current) => ({ ...current, testLeadCount: Number(result.totalTestLeadCount || 0) }));
  };

  const performPublish = async () => {
    setSaving(true);
    try {
      const response = await updateSurvey(surveyId, { ...surveyPayload(form), status: "published" });
      setForm(surveyToForm(response.data));
      setDirty(false);
      setSelectedQuestionId("home");
      setTab("overview");
      notify("Survey published successfully.");
      const overviewResponse = await getSurveyOverview(surveyId);
      setOverview({ ...emptyOverview, ...overviewResponse.data });
      setConfirmation(null);
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to publish the survey."), "error");
    }
    setSaving(false);
  };

  const inactivate = () => {
    setConfirmation({
      action: "inactivate",
      title: "Inactivate Survey?",
      message: `Make “${form.title}” inactive? Customers will no longer see it.`,
      confirmLabel: "Inactivate",
      confirmStyle: "warning",
    });
  };

  const performInactivate = async () => {
    setSaving(true);
    try {
      const response = await updateSurvey(surveyId, { status: "inactive" });
      setForm(surveyToForm(response.data));
      notify("Survey made inactive successfully.");
      setConfirmation(null);
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to inactivate the survey."), "error");
    }
    setSaving(false);
  };

  const duplicate = () => {
    if (form.status === "draft" && dirty) return;
    history.push({ pathname: "/admin/surveys/new", state: { duplicateDraft: duplicateSurveyForm(form) } });
  };

  const remove = () => {
    const unsavedWarning = dirty ? " Unsaved changes will also be discarded." : "";
    setConfirmation({
      action: "delete",
      title: "Delete Survey?",
      message: `Delete “${form.title}”? The survey will be hidden, but its historical submissions will remain stored.${unsavedWarning}`,
      confirmLabel: "Delete",
      confirmStyle: "danger",
    });
  };

  const performRemove = async () => {
    setSaving(true);
    try {
      await deleteSurvey(surveyId);
      leaveFor("/admin/surveys");
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to delete the survey."), "error");
      setSaving(false);
    }
  };

  const readOnly = !isNew && form.status !== "draft";
  const showBuilder = isNew || form.status === "draft";
  const showSubmissions = !isNew && (form.status === "published" || form.status === "inactive");
  const showStats = form.status === "published" || form.status === "inactive";
  const questions = form.questions || [];
  const busy = loading || saving || submissionsLoading || detailLoading;
  const detailActions = [
    form.status === "draft" && {
      key: "deploy",
      label: "Deploy Test",
      icon: "fa-flask",
      disabled: saving,
      onSelect: openTestDeployment,
    },
    !(form.status === "draft" && dirty) && {
      key: "duplicate",
      label: "Duplicate",
      icon: "fa-copy",
      disabled: saving,
      onSelect: duplicate,
    },
    {
      key: "delete",
      label: "Delete",
      icon: "fa-trash",
      danger: true,
      dividerBefore: true,
      disabled: saving,
      onSelect: remove,
    },
  ];

  return (
    <div className="main-content survey-engine survey-engine-detail-page">
      {busy && <ScreenLoader />}
      <NotificationSystem ref={notificationRef} />
      <Grid fluid><Row><Col md={12}>
        <div className={`survey-engine-page-header ${showStats ? "survey-engine-page-header-compact" : ""}`}>
          <div>
            <Button className="survey-engine-back" icon="fa-arrow-left" onClick={() => history.push("/admin/surveys")}>Back to surveys</Button>
            <h3>{form.title || (isNew ? "Create Survey" : "Survey")}</h3>
            <p className={`survey-engine-header-description ${form.description ? "" : "placeholder"}`}>
              {form.description || "Add a survey description from the Home Card settings."}
            </p>
            <Status value={form.status} />
            {!isNew && form.status === "draft" && <span className="survey-test-lead-count">Test leads: {Number(form.testLeadCount || 0)}</span>}
          </div>
          <div className="survey-engine-header-actions">
            <div className="survey-engine-header-lifecycle-actions">
              {isNew && <Button bsStyle="primary" icon="fa-save" solid disabled={saving} onClick={saveDraft}>Save Draft</Button>}
              {!isNew && form.status === "draft" && <Button bsStyle="success" icon="fa-paper-plane" disabled={saving} onClick={publish}>Publish</Button>}
              {!isNew && form.status === "published" && <Button bsStyle="warning" icon="fa-ban" disabled={saving} onClick={inactivate}>Inactivate</Button>}
            </div>
            <div className="survey-engine-header-secondary-actions">
              {!isNew && form.status === "draft" && <Button bsStyle="primary" icon="fa-save" solid disabled={saving} onClick={saveDraft}>Save Draft</Button>}
              {!isNew && <SurveyActionMenu id={`survey-detail-actions-${surveyId}`} actions={detailActions} disabled={saving} />}
            </div>
          </div>
        </div>

        {!showBuilder && (
          <div className="survey-engine-tabs">
            <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>Overview</button>
            {showSubmissions && <button className={tab === "submissions" ? "active" : ""} onClick={() => setTab("submissions")}>Submissions ({form.submissionCount || 0})</button>}
          </div>
        )}

        {!showBuilder && tab === "overview" && (
          <>
            {showStats && (
              <SurveyKpiGrid overview={overview} />
            )}
            <SurveyOverviewWorkspace form={form} selectedQuestionId={selectedQuestionId} onSelect={setSelectedQuestionId} />
          </>
        )}

        {showBuilder && (
          <SurveyBuilderWorkspace form={form} errors={formErrors} readOnly={readOnly} selectedQuestionId={selectedQuestionId} onSelect={setSelectedQuestionId} onChange={updateForm} />
        )}

        {showSubmissions && tab === "submissions" && (
          <SurveySubmissionTable submissions={submissions} loading={submissionsLoading} onPage={loadSubmissions} onOpen={openSubmission} />
        )}
      </Col></Row></Grid>

      <SurveySubmissionDetailModal submission={submissionDetail} onClose={() => setSubmissionDetail(null)} />
      <SurveyConfirmationModal
        show={Boolean(confirmation)}
        title={confirmation && confirmation.title}
        message={confirmation && confirmation.message}
        confirmLabel={confirmation && confirmation.confirmLabel}
        confirmStyle={confirmation && confirmation.confirmStyle}
        busy={saving}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirmAction}
      />
      <SurveyTestDeploymentModal
        show={testDeploymentOpen}
        surveyId={surveyId}
        onHide={() => setTestDeploymentOpen(false)}
        onDeployed={testDeployed}
        onAssignmentRemoved={testAssignmentRemoved}
      />
    </div>
  );
};

export default SurveyDetailPage;
